import React, { useState, useEffect } from "react";
import axios from "axios";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { FileText, CheckCircle, XCircle, Clock, AlertCircle, RotateCcw } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Bump `courtesy_excusals_used` on the student record by hitting the dedicated
// backend endpoint (the generic entities router blocks direct Student updates).
const bumpCourtesyCounter = async (studentEmail) => {
  const students = await base44.entities.Student.list();
  const student = students.find(
    (s) => s.email?.toLowerCase() === studentEmail?.toLowerCase(),
  );
  if (!student) return;
  const nextCount = (student.courtesy_excusals_used || 0) + 1;
  await axios.patch(
    `${BACKEND_URL}/api/students/${encodeURIComponent(student.student_id)}`,
    { courtesy_excusals_used: nextCount },
    { withCredentials: true },
  );
};

export default function TeacherExcusals() {
  const [reviewingExcusal, setReviewingExcusal] = useState(null);
  const [teacherNotes, setTeacherNotes] = useState("");
  const [useCourtesy, setUseCourtesy] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });

  const timeSettings = settings.find(s => s.setting_key === 'time_constraints') || {
    teacher_approval_block_start: "11:00",
    teacher_approval_block_end: "16:45",
    auto_deny_deadline: "11:00"
  };

  // Convert 24-hour time to 12-hour format
  const formatTime12Hour = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
  };

  // Check if user has teacher access
  const isTeacher = user?.role === 'admin' || (user?.role === 'user' && user?.user_type === 'teacher');
  // Only admins & assistant principals can see / apply courtesy excusals
  const isAdminOrAP =
    user?.role === 'admin' ||
    (user?.role === 'user' && user?.user_type === 'assistant_principal');

  if (!isTeacher) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const teacherEmail = user?.email;

  const { data: myExcusals = [], isLoading } = useQuery({
    queryKey: ['myExcusals', user?.role, teacherEmail],
    queryFn: () => {
      if (user?.role === 'admin') {
        return base44.entities.ExcusalSubmission.list('-created_date');
      }
      return base44.entities.ExcusalSubmission.filter({ teacher_email: teacherEmail }, '-created_date');
    },
    enabled: !!user && (user?.role === 'admin' || !!teacherEmail),
  });

  // Auto-deny excusals past deadline
  useEffect(() => {
    const autoDenyPastDeadline = async () => {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      const [deadlineHour, deadlineMin] = timeSettings.auto_deny_deadline.split(':').map(Number);
      const deadlineTime = deadlineHour * 60 + deadlineMin;
      
      // If current time is past the deadline
      if (currentTime >= deadlineTime) {
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const pendingExcusals = myExcusals.filter(e => {
          if (e.status !== 'pending') return false;
          
          const submissionDate = new Date(e.submission_date);
          // Check if submission is from today or earlier
          return submissionDate < todayStart || 
                 (submissionDate.toDateString() === todayStart.toDateString());
        });
        
        for (const excusal of pendingExcusals) {
          await base44.entities.ExcusalSubmission.update(excusal.id, {
            status: 'denied',
            reviewed_date: format(now, 'yyyy-MM-dd'),
            teacher_notes: 'No Teacher Response'
          });
        }
        
        if (pendingExcusals.length > 0) {
          queryClient.invalidateQueries(['myExcusals']);
        }
      }
    };
    
    if (myExcusals.length > 0) {
      autoDenyPastDeadline();
    }
  }, [myExcusals, timeSettings.auto_deny_deadline, queryClient]);

  const isWithinBlockedTime = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = timeSettings.teacher_approval_block_start.split(':').map(Number);
    const [endHour, endMin] = timeSettings.teacher_approval_block_end.split(':').map(Number);
    
    const blockStart = startHour * 60 + startMin;
    const blockEnd = endHour * 60 + endMin;
    
    return currentTime >= blockStart && currentTime < blockEnd;
  };

  const reviewExcusalMutation = useMutation({
    mutationFn: async ({ excusalId, status, notes, useCourtesy }) => {
      const excusal = myExcusals.find(e => e.id === excusalId);
      
      await base44.entities.ExcusalSubmission.update(excusalId, {
        status: status,
        reviewed_date: format(new Date(), 'yyyy-MM-dd'),
        teacher_notes: notes,
        courtesy_excusal_used: useCourtesy || false
      });

      if (status === 'approved') {
        if (useCourtesy) {
          // Courtesy excusal: mark the student EXCUSED on the current roster
          // (top-level status='excused' matches the manual-E path the closeout logic archives).
          await base44.entities.RosterEntry.update(excusal.roster_entry_id, {
            status: 'excused'
          });

          // Increment student courtesy counter (shown on Student Dashboard)
          await bumpCourtesyCounter(excusal.student_email);
        } else {
          await base44.entities.RosterEntry.update(excusal.roster_entry_id, {
            status: 'excused'
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myExcusals']);
      queryClient.invalidateQueries(['myRosterEntries']);
      queryClient.invalidateQueries(['studentRecord']);
      setReviewingExcusal(null);
      setTeacherNotes("");
    },
  });

  // Admin / AP override: apply a courtesy excusal to any excusal regardless of
  // its current status (pending / approved / denied). Marks the current roster
  // entry E and bumps the student's courtesy counter.
  const applyCourtesyMutation = useMutation({
    mutationFn: async (excusalId) => {
      const excusal = myExcusals.find(e => e.id === excusalId);
      if (!excusal) throw new Error('Excusal not found');

      await base44.entities.ExcusalSubmission.update(excusalId, {
        status: 'approved',
        reviewed_date: format(new Date(), 'yyyy-MM-dd'),
        teacher_notes: `${excusal.teacher_notes ? excusal.teacher_notes + ' · ' : ''}Courtesy excusal applied by ${user?.email || 'admin'}`,
        courtesy_excusal_used: true,
      });

      await base44.entities.RosterEntry.update(excusal.roster_entry_id, {
        status: 'excused'
      });

      await bumpCourtesyCounter(excusal.student_email);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myExcusals']);
      queryClient.invalidateQueries(['myRosterEntries']);
      queryClient.invalidateQueries(['studentRecord']);
    },
  });

  // Admin / AP override: send a denied excusal back to Pending so it can be
  // reviewed again. Only clears the denial-specific fields; nothing else.
  const reopenExcusalMutation = useMutation({
    mutationFn: async (excusalId) => {
      await base44.entities.ExcusalSubmission.update(excusalId, {
        status: 'pending',
        reviewed_date: null,
        teacher_notes: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myExcusals']);
    },
  });

  const handleApprove = (excusalId) => {
    if (isWithinBlockedTime()) {
      alert(`Approvals are not available between ${formatTime12Hour(timeSettings.teacher_approval_block_start)} and ${formatTime12Hour(timeSettings.teacher_approval_block_end)}`);
      return;
    }
    reviewExcusalMutation.mutate({
      excusalId,
      status: 'approved',
      notes: teacherNotes,
      useCourtesy: isAdminOrAP ? useCourtesy : false,
    });
  };

  const handleDeny = (excusalId) => {
    if (isWithinBlockedTime()) {
      alert(`Denials are not available between ${formatTime12Hour(timeSettings.teacher_approval_block_start)} and ${formatTime12Hour(timeSettings.teacher_approval_block_end)}`);
      return;
    }
    if (!teacherNotes.trim()) {
      alert("Please provide a reason for denying this excusal request");
      return;
    }
    reviewExcusalMutation.mutate({
      excusalId,
      status: 'denied',
      notes: teacherNotes
    });
  };

  const pendingExcusals = myExcusals.filter(e => e.status === 'pending');
  const reviewedExcusals = myExcusals.filter(e => e.status !== 'pending');
  const blockedTime = isWithinBlockedTime();

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Excusal Requests</h1>
          <p className="text-slate-600 mt-1">{user?.role === 'admin' ? 'Review and Approve All Student Excusal Requests' : 'Review and Approve Student Excusal Requests'}</p>
        </div>

        {!teacherEmail && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Your account does not have an email address. Please contact an administrator.
            </AlertDescription>
          </Alert>
        )}

        {blockedTime && (
          <Alert className="bg-orange-50 border-orange-200">
            <Clock className="w-4 h-4 text-orange-700" />
            <AlertDescription className="text-orange-900">
              <strong>Approval/Denial window is closed.</strong> You can review excusal requests up until {formatTime12Hour(timeSettings.auto_deny_deadline)}. Unapproved excusals will be auto denied at {formatTime12Hour(timeSettings.auto_deny_deadline)}.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-700 to-red-800 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Pending Review</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{pendingExcusals.length}</div>
                <Clock className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">Awaiting your review</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Approved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{myExcusals.filter(e => e.status === 'approved').length}</div>
                <CheckCircle className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">Total approved</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-800 to-red-900 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Denied</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{myExcusals.filter(e => e.status === 'denied').length}</div>
                <XCircle className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">Total denied</p>
            </CardContent>
          </Card>
        </div>

        {/* Unified Excusal Requests Table (replaces old pending + reviewed cards) */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Excusal Requests ({myExcusals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myExcusals.length === 0 && !isLoading ? (
              <div className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Excusal Requests</h3>
                <p className="text-slate-500">You have no excusal requests to review.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead className="min-w-[220px]">Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myExcusals.map((excusal) => {
                      const ts = excusal.created_date || excusal.submission_date;
                      const dateStr = ts ? format(new Date(ts), 'MMM d, yyyy') : '-';
                      const tsStr = ts ? format(new Date(ts), 'MMM d, yyyy h:mm a') : '-';
                      return (
                        <TableRow key={excusal.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">
                            <div>{excusal.student_name || '-'}</div>
                            {excusal.subject && (
                              <div className="text-xs text-slate-500">{excusal.subject}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">{dateStr}</TableCell>
                          <TableCell className="text-xs text-slate-500 whitespace-nowrap">{tsStr}</TableCell>
                          <TableCell className="text-sm text-slate-700">
                            <div className="max-w-md whitespace-pre-wrap">{excusal.reason || '-'}</div>
                            {excusal.proof_of_completion_url && (
                              <a
                                href={excusal.proof_of_completion_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 underline mt-1 inline-block"
                              >
                                View proof
                              </a>
                            )}
                            {excusal.teacher_notes && (
                              <div className="text-xs text-slate-500 mt-1 italic">
                                Notes: {excusal.teacher_notes}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {excusal.status === 'pending' && (
                              <Badge className="bg-orange-500 text-white">Pending</Badge>
                            )}
                            {excusal.status === 'approved' && (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            )}
                            {excusal.status === 'denied' && (
                              <Badge className="bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3 mr-1" />
                                Denied
                              </Badge>
                            )}
                            {excusal.courtesy_excusal_used && (
                              <Badge className="bg-blue-100 text-blue-800 ml-1">Courtesy</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end flex-wrap gap-2">
                              {excusal.status === 'pending' && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setReviewingExcusal(excusal.id);
                                    setTeacherNotes("");
                                    setUseCourtesy(false);
                                  }}
                                  className="bg-red-700 hover:bg-red-800 text-white"
                                  data-testid={`review-excusal-${excusal.id}`}
                                >
                                  <FileText className="w-3 h-3 mr-1" /> Review
                                </Button>
                              )}
                              {excusal.status === 'denied' && isAdminOrAP && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (window.confirm(
                                      `Re-open this denied excusal for ${excusal.student_name}? ` +
                                      `Its status will reset to Pending so it can be reviewed again.`
                                    )) {
                                      reopenExcusalMutation.mutate(excusal.id);
                                    }
                                  }}
                                  disabled={reopenExcusalMutation.isPending}
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                  data-testid={`reopen-excusal-${excusal.id}`}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" /> Re-open
                                </Button>
                              )}
                              {excusal.status === 'approved' && isAdminOrAP && !excusal.courtesy_excusal_used && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (window.confirm(
                                      `Apply a courtesy excusal for ${excusal.student_name}? ` +
                                      `The student will be marked E on the current roster and their courtesy counter will be incremented.`
                                    )) {
                                      applyCourtesyMutation.mutate(excusal.id);
                                    }
                                  }}
                                  disabled={applyCourtesyMutation.isPending}
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                  data-testid={`apply-courtesy-${excusal.id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {applyCourtesyMutation.isPending ? 'Applying...' : 'Apply Courtesy'}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Review dialog for pending excusals */}
        <Dialog
          open={!!reviewingExcusal}
          onOpenChange={(open) => {
            if (!open) {
              setReviewingExcusal(null);
              setTeacherNotes("");
              setUseCourtesy(false);
            }
          }}
        >
          <DialogContent className="max-w-lg">
            {(() => {
              const excusal = myExcusals.find(e => e.id === reviewingExcusal);
              if (!excusal) return null;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle>Review Excusal — {excusal.student_name}</DialogTitle>
                    <DialogDescription>
                      <strong>Subject:</strong> {excusal.subject || '-'}<br />
                      <strong>Reason:</strong> {excusal.reason || '-'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <Textarea
                      value={teacherNotes}
                      onChange={(e) => setTeacherNotes(e.target.value)}
                      placeholder="Add notes (required for denial, optional for approval)..."
                      className="h-24"
                    />
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      {isAdminOrAP ? (
                        <>
                          <input
                            type="checkbox"
                            id={`courtesy-${excusal.id}`}
                            checked={useCourtesy}
                            onChange={(e) => setUseCourtesy(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <label htmlFor={`courtesy-${excusal.id}`} className="text-sm font-medium text-slate-700">
                            Use Courtesy Excusal (student marked <strong>E</strong> + counter incremented)
                          </label>
                        </>
                      ) : (
                        <span className="text-sm text-slate-600">
                          Approval will mark the student <strong>E</strong> on the current roster.
                        </span>
                      )}
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button
                      onClick={() => handleApprove(excusal.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={reviewExcusalMutation.isPending || blockedTime}
                      data-testid={`approve-excusal-${excusal.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                    <Button
                      onClick={() => handleDeny(excusal.id)}
                      className="bg-red-700 hover:bg-red-800 text-white"
                      disabled={reviewExcusalMutation.isPending || blockedTime}
                      data-testid={`deny-excusal-${excusal.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Deny
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}